import { Control } from 'react-hook-form';
import Input from 'components/Common/Input';
import { Box, Typography, Button, Paper, CircularProgress, useTheme } from '@mui/material';
import { FaChevronRight } from 'react-icons/fa';
import Select from '@/components/Common/Select';
import { useGetPublicCountries } from '@/apis/agency/businessOnboarding/hooks';

interface StepProps {
  onNext: () => void | Promise<void>;
  control: Control<any>;
  isSubmitting?: boolean;
}

const AddressStep: React.FC<StepProps> = ({ onNext, control, isSubmitting = false }: StepProps) => {
  const theme = useTheme();
  const { data: countries, isLoading: isCountriesLoading } = useGetPublicCountries();

  return (
    <Paper sx={{ p: 3, borderRadius: 3, width: '100%' }}>
      <Typography fontWeight={600} sx={{ fontSize: { xs: '16px', md: '24px' } }}>
        What's your business address?
      </Typography>

      <Typography lineHeight='normal' fontSize='14px' color='text.secondary' mt={1} mb={1}>
        Your AI agent will use this when customers ask for your location or directions.
      </Typography>

      <Input
        name='address.street_address'
        control={control}
        fullWidth
        required
        rules={{ required: 'Street address is required' }}
        label='Street Address'
        placeholder='e.g. 123 Main Street, Suite 100'
        margin='normal'
        sx={{ mt: 3 }}
      />

      <Box display='flex' gap={2} mt={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Input name='address.city' control={control} fullWidth label='City' />
        <Input name='address.state_province_region' control={control} fullWidth label='State' />
      </Box>

      <Box display='flex' gap={2} mt={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Input name='address.postal_zip_code' control={control} fullWidth label='Zip/Postal' />
        <Select
          name='address.country'
          label='Country'
          isLoading={isCountriesLoading}
          control={control}
          options={countries || []}
          fullWidth
        />
      </Box>

      <Button
        variant='contained'
        fullWidth
        disabled={isSubmitting}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          background: theme.palette.primary.main,
          mt: 3
        }}
        onClick={onNext}
      >
        {isSubmitting ? (
          <CircularProgress size={20} color='inherit' />
        ) : (
          <>
            Continue <FaChevronRight />
          </>
        )}
      </Button>
    </Paper>
  );
};

export default AddressStep;
